import { Dispatch, SetStateAction, useCallback } from "react"
import { AppEvent } from "../types"
import { useUpdateEventMutation } from "@/api/event/update"
import { useNavigate } from "react-router-dom"
import dayjs from "dayjs"
import { useQueryClient } from "@tanstack/react-query"
import { HistoryLogEntityType } from "../../../../../backend/src/types"
import queryKeys from "@/api/queryKeys"
import _ from "lodash"
import { useMarkAllReadEventsMutation } from "@/api/event/mark-all-read"

export type AppEventProviderActionsProps = {
    handleMarkAsRead: (event: AppEvent) => Promise<void>
    handleMarkAllAsRead: () => Promise<void>
    addEvent: (event: AppEvent) => Promise<void>
    gotoEvent: (event: AppEvent) => void
}

const useAppEventProviderActions = (events: AppEvent[], setEvents: Dispatch<SetStateAction<AppEvent[]>>): AppEventProviderActionsProps => {
    const navigate = useNavigate()
    const gotoEvent = useCallback(
        (event: AppEvent) => {
            setEvents((prev) => prev.map((ev) => (ev.id === event.id ? { ...ev, read: true } : ev)))
            if (event.href) {
                navigate(event.href)
            }
        },
        [navigate, setEvents]
    )

    const { mutateAsync: updateEvent } = useUpdateEventMutation()
    const handleMarkAsRead = useCallback(
        async (event: AppEvent) => {
            await updateEvent({ id: event.id, markAsRead: true })
            setEvents((prev) => prev.map((ev) => (ev.id === event.id ? { ...ev, readAt: dayjs() } : ev)))
            gotoEvent(event)
        },
        [gotoEvent, updateEvent, setEvents]
    )

    const { mutateAsync: markAllAsRead } = useMarkAllReadEventsMutation()
    const handleMarkAllAsRead = useCallback(async () => {
        await markAllAsRead({ eventIds: events.map((ev) => ev.id) })
        setEvents((prev) => prev.map((ev) => ({ ...ev, readAt: dayjs() })))
    }, [markAllAsRead, events, setEvents])

    // const notifyAudio = useMemo(() => new Audio("/notify.mp3"), [])
    // const notify = useMemo(() => _.debounce(() => notifyAudio.play(), 300), [notifyAudio])

    const client = useQueryClient()
    const addEvent = useCallback(
        async (event: AppEvent) => {
            setEvents((prev) => [event, ...prev.filter((ev) => ev.id !== event.id)])
            await client.invalidateQueries({ queryKey: queryKeys.dashboard.info() })
            switch (event.entityType) {
                case HistoryLogEntityType.INVOICE:
                    await client.invalidateQueries({ queryKey: queryKeys.invoice.all() })
                    await client.invalidateQueries({ queryKey: queryKeys.invoice.metadata(Number(event.entityId)) })
                    await client.invalidateQueries({ queryKey: queryKeys.invoice.detail(Number(event.entityId)) })
                    break
                case HistoryLogEntityType.PAYMENT_REQUEST:
                    await client.invalidateQueries({ queryKey: queryKeys.paymentRequest.all() })
                    await client.invalidateQueries({ queryKey: queryKeys.paymentRequest.detail(Number(event.entityId)) })
                    break
            }
            //if (!event.readAt) notify()
        },
        [client, setEvents]
    )

    return {
        handleMarkAsRead,
        handleMarkAllAsRead,
        addEvent,
        gotoEvent,
    }
}
export default useAppEventProviderActions
